import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート

// --- Components ---
function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, currentItemCount }) {
    if (totalPages <= 1) return null;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = startItem + currentItemCount - 1;
    return (
        <div className="flex flex-col gap-3 mt-4 p-4 border-t sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-gray-700">
                {totalItems > 0 ? `${totalItems.toLocaleString()} 件中 ${startItem.toLocaleString()} - ${endItem.toLocaleString()} 件を表示` : '該当する問い合わせはありません'}
            </span>
            <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">前へ</button>
                <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">次へ</button>
            </div>
        </div>
    );
}

function EditModal({ isOpen, inquiry, onClose, onSave }) {
    const [status, setStatus] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (inquiry) {
            setStatus(inquiry.status);
            setNotes(inquiry.notes || '');
        }
    }, [inquiry]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave(inquiry.id, { status, notes });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-stretch z-50 p-0 sm:items-center sm:p-4">
            <div className="bg-white rounded-none shadow-2xl w-full max-w-lg overflow-y-auto p-4 sm:rounded-lg sm:p-6">
                <h3 className="text-lg font-bold mb-4">問い合わせ内容編集</h3>
                <div className="space-y-4">
                    <div><span className="font-semibold">会社名:</span> {inquiry.company_name}</div>
                    <div><span className="font-semibold">担当者名:</span> {inquiry.contact_name}</div>
                    <div><span className="font-semibold">Email:</span> {inquiry.email}</div>
                    <div><span className="font-semibold">電話番号:</span> {inquiry.phone}</div>
                    <div className="p-2 bg-gray-50 rounded-md"><span className="font-semibold">内容:</span><br/>{inquiry.message}</div>
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">対応状況</label>
                        <select id="status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-1 p-2 border rounded-md">
                            <option value="未対応">未対応</option>
                            <option value="対応中">対応中</option>
                            <option value="対応完了">対応完了</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">管理者用メモ</label>
                        <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows="4" className="w-full mt-1 p-2 border rounded-md"></textarea>
                    </div>
                </div>
                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">キャンセル</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-md">保存</button>
                </div>
            </div>
        </div>
    );
}


function InquiryManagementPage() {
    const { user } = useOutletContext();
    const [inquiries, setInquiries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [editingInquiry, setEditingInquiry] = useState(null);
    const API_URL = "https://salesbreaker.jp";

    useEffect(() => {
        const handler = setTimeout(() => {
          setDebouncedSearchTerm(searchTerm);
          setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchInquiries = useCallback(async () => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: currentPage,
            limit: 50,
            searchTerm: debouncedSearchTerm,
            statusFilter: statusFilter,
        });
        try {
            const res = await fetch(`${API_URL}/api/admin/inquiries?${params.toString()}`, {
                headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
            });
            const data = await res.json();
            if (data.success) {
                setInquiries(data.inquiries);
                setPagination(data.pagination);
            } else {
                alert('問い合わせの取得に失敗しました。');
            }
        } catch (error) {
            console.error(error);
            alert('通信エラーが発生しました。');
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearchTerm, statusFilter]);

    useEffect(() => {
        fetchInquiries();
    }, [fetchInquiries]);

    const handleSaveModal = async (id, data) => {
        try {
            const res = await fetch(`${API_URL}/api/admin/inquiries/${id}`, {
                method: 'PUT',
                headers: { // ★ 2. 認証ヘッダーを追加
                    'Content-Type': 'application/json',
                    ...getAuthHeader(),
                },
                body: JSON.stringify(data),
            });
            const result = await res.json();
            if (result.success) {
                setEditingInquiry(null);
                fetchInquiries();
            } else {
                alert('更新に失敗しました。');
            }
        } catch (error) {
            alert('通信エラーが発生しました。');
        }
    };

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">📞 問い合わせ管理</h1>
            <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                        type="text"
                        placeholder="会社名、氏名、連絡先などで検索..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border rounded-md md:col-span-2"
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="w-full p-2 border rounded-md bg-white"
                    >
                        <option value="">全てのステータス</option>
                        <option value="未対応">未対応</option>
                        <option value="対応中">対応中</option>
                        <option value="対応完了">対応完了</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {isLoading ? <div className="p-8 text-center">読み込み中...</div> : (
                    <>
                        <div className="overflow-x-auto">
                            <div className="divide-y divide-gray-100 md:hidden">
                                {inquiries.length > 0 ? inquiries.map(inquiry => (
                                    <div key={`${inquiry.id}-mobile`} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-900 break-words">{inquiry.company_name || '会社名なし'}</p>
                                                <p className="mt-1 text-xs text-gray-500">{new Date(inquiry.created_at).toLocaleString('ja-JP')}</p>
                                            </div>
                                            <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">{inquiry.status}</span>
                                        </div>
                                        <div className="mt-3 space-y-1 text-sm text-gray-600">
                                            <p>{inquiry.contact_name || '担当者名なし'}</p>
                                            <p className="break-all">{inquiry.email}</p>
                                            <p>{inquiry.phone}</p>
                                        </div>
                                        <button onClick={() => setEditingInquiry(inquiry)} className="mt-4 w-full rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100">詳細・編集</button>
                                    </div>
                                )) : (
                                    <div className="px-4 py-10 text-center text-sm text-gray-500">該当する問い合わせはありません。</div>
                                )}
                            </div>
                            <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                                    <tr>
                                        <th className="py-3 px-4">日時</th>
                                        <th className="py-3 px-4">会社名</th>
                                        <th className="py-3 px-4">担当者名</th>
                                        <th className="py-3 px-4">連絡先</th>
                                        <th className="py-3 px-4">ステータス</th>
                                        <th className="py-3 px-4">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {inquiries.map(inquiry => (
                                        <tr key={inquiry.id}>
                                            <td className="py-3 px-4">{new Date(inquiry.created_at).toLocaleString('ja-JP')}</td>
                                            <td className="py-3 px-4">{inquiry.company_name}</td>
                                            <td className="py-3 px-4">{inquiry.contact_name}</td>
                                            <td className="py-3 px-4">{inquiry.email}<br/>{inquiry.phone}</td>
                                            <td className="py-3 px-4">{inquiry.status}</td>
                                            <td className="py-3 px-4">
                                                <button onClick={() => setEditingInquiry(inquiry)} className="text-indigo-600 hover:underline">詳細・編集</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination 
                            currentPage={pagination ? pagination.currentPage : 1}
                            totalPages={pagination ? pagination.totalPages : 1}
                            onPageChange={setCurrentPage}
                            totalItems={pagination ? pagination.totalItems : 0}
                            itemsPerPage={pagination ? pagination.itemsPerPage : 50}
                            currentItemCount={inquiries.length}
                        />
                    </>
                )}
            </div>

            <EditModal 
                isOpen={!!editingInquiry}
                inquiry={editingInquiry}
                onClose={() => setEditingInquiry(null)}
                onSave={handleSaveModal}
            />
        </div>
    );
}

export default InquiryManagementPage;
